import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { pairingFeature } from '../../../../features/pairing/contract'
import { ConnectionType } from '../../../../types/connection'
import { delay } from '../../../../utils/TimeUtils'
import {
  createTelinkKeyboardControlPayload,
  createTelinkKeyboardPairingExitCommand,
  createTelinkKeyboardPairingModeCommand,
  createTelinkKeyboardPairingStatusCommand,
  TELINK_KEYBOARD_CONTROL_STATUS,
  TELINK_KEYBOARD_HID,
  TELINK_RECEIVER_KEYBOARD_DEVICE_TYPE,
} from '../../protocols/keyboard-command'

/**
 * Telink 键盘接收器配对能力。
 *
 * 对码由接收器发起，键盘需在接收器进入等待状态后按产品说明书的 Fn + 4 进入 2.4G 对码。
 */
export class TelinkKeyboardPairing extends pairingFeature {
  /** V700 产品说明书规定键盘对码窗口为 30 秒。 */
  private static readonly pairingTimeoutMs = 30_000
  private static readonly pollingIntervalMs = 200

  public async start(): Promise<boolean> {
    this.assertReceiverConnection()

    const wirelessProductId = this.getWirelessProductId()
    const stopInputReportDiagnostics = this.startInputReportDiagnostics()
    console.info('[TelinkKeyboardPairing] 进入键盘对码模式', {
      wirelessProductId: this.formatProductId(wirelessProductId),
      command: this.toHex(createTelinkKeyboardPairingModeCommand(wirelessProductId)),
    })

    try {
      await this.sendControlCommand(createTelinkKeyboardPairingModeCommand(wirelessProductId), true)
      return await this.waitForPairingResult()
    } finally {
      // Hub 1.0 在成功、超时和进入失败后都会发 A1 84；这里保留原始错误，退出失败只记录现场。
      await this.exitPairingMode()
      stopInputReportDiagnostics()
    }
  }

  /**
   * 文档将 A1 01 的完整状态定义为 Input Report；记录该通道可区分“未上报”与“Feature 回包被截短”。
   */
  private startInputReportDiagnostics(): () => void {
    let reportCount = 0
    const expectedReportId = TELINK_KEYBOARD_HID.controlOutputReportId
    const listener = (reportId: number, data: DataView) => {
      if (reportId !== expectedReportId) return

      reportCount += 1
      console.info('[TelinkKeyboardPairing] Input 状态回包', {
        reportId: `0x${reportId.toString(16).padStart(2, '0')}`,
        response: this.toHex(data),
        byteLength: data.byteLength,
      })
    }

    this.device.channel.onInputReport(listener)
    return () => {
      this.device.channel.offInputReport(listener)
      console.info('[TelinkKeyboardPairing] Input 状态回包汇总', {
        reportId: `0x${expectedReportId.toString(16).padStart(2, '0')}`,
        reportCount,
      })
    }
  }

  /**
   * 读取当前清单的无线 PID。
   *
   * 配对页面已用同一份 manifest 将有线键盘与接收器配成候选项；Feature 只消费这个既有约定，
   * 不新增产品 ID 参数，也不承担 PID 切换后的设备重新枚举。
   */
  private getWirelessProductId(): number {
    const productIds = Array.isArray(this.device.manifest.hardware.productId)
      ? this.device.manifest.hardware.productId
      : [this.device.manifest.hardware.productId]
    const wirelessProductId = productIds[1]

    if (
      !Number.isInteger(wirelessProductId) ||
      wirelessProductId < 0 ||
      wirelessProductId > 0xffff
    ) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 键盘清单缺少有效的无线 PID')
    }

    return wirelessProductId
  }

  /** 通过现有 Output + Feature 交互读取控制命令确认，避免引入运行时专用通道。 */
  private async sendControlCommand(command: Uint8Array, shouldLog = false): Promise<DataView> {
    // pairing 命令（A0/A1）是接收器原生命令，不经键盘透传，无需无线命令偏移。
    const response = await this.device.sendAndWait(
      TELINK_KEYBOARD_HID.controlOutputReportId,
      createTelinkKeyboardControlPayload(command),
      TELINK_KEYBOARD_HID.controlResponseFeatureReportId,
      (data) => data.byteLength > 0 && data.getUint8(0) !== TELINK_KEYBOARD_CONTROL_STATUS.pending,
      { skipCmdOffset: true, maxRetries: 3 },
    )

    if (shouldLog) {
      console.info('[TelinkKeyboardPairing] 接收器控制回包', {
        command: this.toHex(command),
        response: this.toHex(response),
      })
    }

    if (response.getUint8(0) !== TELINK_KEYBOARD_CONTROL_STATUS.acknowledged) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `Telink 接收器拒绝控制命令：${this.toHex(command)}`,
      )
    }

    return response
  }

  /** 在产品规定的对码窗口内读取接收器状态。 */
  private async waitForPairingResult(): Promise<boolean> {
    const deadline = Date.now() + TelinkKeyboardPairing.pairingTimeoutMs
    let attempt = 0

    while (Date.now() < deadline) {
      attempt += 1
      const response = await this.sendControlCommand(createTelinkKeyboardPairingStatusCommand())
      const paired = this.isKeyboardPaired(response)
      const status = this.toHex(response)

      // 对码问题排查期间保留每一轮原始回包，便于确认是否出现短暂的 K_FLAG 状态变化。
      console.info('[TelinkKeyboardPairing] 对码状态回包', {
        attempt,
        response: status,
        byteLength: response.byteLength,
        paired,
      })

      if (paired) return true
      await delay(TelinkKeyboardPairing.pollingIntervalMs)
    }

    console.warn('[TelinkKeyboardPairing] 对码超时：接收器未确认键盘已配对')
    return false
  }

  /** 对码窗口结束后只退出状态；V700 键盘流程不发送清除、随机 RF 或接收器复位命令。 */
  private async exitPairingMode(): Promise<void> {
    try {
      await this.sendControlCommand(createTelinkKeyboardPairingExitCommand(), true)
    } catch (error) {
      console.warn('[TelinkKeyboardPairing] 退出对码模式失败', error)
    }
  }

  /**
   * V700 接收器通过 Feature 状态邮箱返回 `ACK DEVICE_TYPE`。
   *
   * `ACK=01` 只表示查询命令执行成功；第二字节为键盘设备类型时，才表示键盘已经完成对码。
   */
  private isKeyboardPaired(response: DataView): boolean {
    return (
      response.byteLength >= 2 &&
      response.getUint8(0) === TELINK_KEYBOARD_CONTROL_STATUS.acknowledged &&
      response.getUint8(1) === TELINK_RECEIVER_KEYBOARD_DEVICE_TYPE
    )
  }

  /** 配对由接收器发起；有线键盘本体不具备该控制入口。 */
  private assertReceiverConnection(): void {
    if (this.device.getConnectionType() !== ConnectionType.Wireless) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '请连接 Telink 键盘接收器后再进行配对')
    }
  }

  private formatProductId(productId: number): string {
    return `0x${productId.toString(16).padStart(4, '0')}`
  }

  private toHex(data: DataView | Uint8Array): string {
    const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join(' ')
  }
}
